import React from 'react';
import { useIntl } from 'react-intl';
import type { CoreApplicationContext } from '@eventbrite/legacy-infra.context-types';
import { ConsumerHeaderSearchBar } from '@eventbrite/consumer-header.global-nav';
import { getLogoInfo, getUserMenuInfo } from '@eventbrite/legacy-infra.site-header';
import classNames from 'classnames';
import type {
    FeatureFlags,
    RequestShape,
    searchBarProps,
    UserShape,
} from '@eventbrite/consumer-header.global-nav/types';
import { GlobalHeaderLogo } from '../GlobalHeaderLogo';
import { GlobalHeaderMenus } from '../GlobalHeaderNavigationBar';
import {
    ConsumerHeaderDesktopLinks,
    ConsumerHeaderMobileLinks,
} from '../ConsumerHeaderLinks/ConsumerHeaderLinks';

import styles from './ConsumerHeader.module.scss';

export interface ConsumerHeaderProps {
    env: CoreApplicationContext['env'] & {
        launchNewSignInSignUpLinks?: boolean;
        isManageOverviewActive?: boolean;
        localeInfo?: Partial<CoreApplicationContext['env']['localeInfo']>;
    };
    request: RequestShape;
    user: UserShape;
    onAdditionalMenuTextItemSelect?: Function;
    isHomePage?: boolean;
    id?: string;
    featureFlags: FeatureFlags;
    hasBreadcrumbs?: boolean;
    searchBar?: searchBarProps;
}
export const ConsumerHeader: React.FunctionComponent<ConsumerHeaderProps> = (props) => {
    const {
        user,
        env,
        id,
        featureFlags,
        hasBreadcrumbs,
        request,
        searchBar,
        isHomePage,
        onAdditionalMenuTextItemSelect,
    } = props;

    const { serverUrl, adminServerUrl, logoutUrl, helpUrls } = env;
    const { path: requestPath } = request;
    const serverUrlLanguageSubdirectoy = env?.localeInfo?.uses_language_subdirectory
        ? `${serverUrl}/${env.localeInfo.locale}`
        : `${serverUrl}`;
    const { formatMessage } = useIntl();

    const userMenuInfo = getUserMenuInfo({
        adminServerUrl,
        featureFlags: {
            ...featureFlags,
            isDetachInterestsEnabled: false,
        },
        logoutUrl,
        requestPath,
        serverUrl,
        showBrowseEvents: true,
        user,
        helpUrls,
        showHelpMenu: featureFlags?.showHelpCenterMenu ?? false,
    });

    const headerClassnames = classNames(styles.root, styles.searchDropdown, {
        [styles.breadcrumbs as string]: hasBreadcrumbs,
    });

    return (
        <header
            id={id || 'global-header'}
            className={headerClassnames}
            data-testid="consumer-header"
        >
            <a className={styles.skipLinks} href="#skip-heading">
                {formatMessage({
                    id: 'Skip Main Navigation',
                    defaultMessage: 'Skip Main Navigation',
                })}
            </a>
            <div className={styles.headerContainer} data-role="header">
                <GlobalHeaderLogo
                    info={getLogoInfo(serverUrlLanguageSubdirectoy, requestPath, {
                        iconWidth: '110px',
                    })}
                />
                <ConsumerHeaderSearchBar user={user} env={env} searchBar={searchBar} />
                <div className={styles.desktopLinksContainer} data-testid="consumer-header-desktop">
                    <ConsumerHeaderDesktopLinks
                        user={user}
                        env={env}
                        featureFlags={featureFlags}
                        requestPath={requestPath}
                        isHomePage={isHomePage}
                        onAdditionalMenuTextItemSelect={onAdditionalMenuTextItemSelect}
                    />
                    <GlobalHeaderMenus userMenuInfo={userMenuInfo} />
                </div>
                <div className={styles.mobileLinksContainer} data-testid="consumer-header-mobile">
                    <ConsumerHeaderMobileLinks
                        user={user}
                        env={env}
                        isHomePage={isHomePage}
                        featureFlags={featureFlags}
                        requestPath={requestPath}
                    />
                </div>
            </div>
            <span id="skip-heading" className={styles.skipHeading}>
                {formatMessage({ id: 'Page Content', defaultMessage: 'Page Content' })}
            </span>
        </header>
    );
};
